import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import "./CreateAccount.scss";
import { AppRoutes, roles } from "@/constants";
import {
  Anchor,
  Center,
  Container,
  Image,
  Loader,
  Notification,
  Stack,
  Text,
} from "@mantine/core";
import { determineUserType, generateWelcomeMessage } from "./utils";
import { safeGetParam } from "@/utils/constants/Authentication";
import { useBranding } from "@/pages/LoginManagement/hooks/useBranding";
import { useGoogleSignIn } from "@/pages/LoginManagement/hooks/useGoogleSignIn";
import GenericLogo from "@/components/Logos/GenericLogo";
import genericLogo from "@/assets/generic-logo.svg";
import { lightenHex } from "@/utils/helper";
import SmartWizLogo from "@/components/Logos/SmartWizLogo";
import { HeaderSection } from "./components/HeaderSection";
import type { BusinessContent, CreateAccountProps } from "./types";
import { DEFAULT_PRIMARY_COLOR } from "./types";
import AccountVerification from "../VerificationPage/components/AccountVerificationScreen";
import { getReferrerBranding } from "@/utils/api/referrals";
import { useAccountCreationStore } from "./store/useAccountCreationStore";
import { useAccountCreation } from "./hooks/useAccountCreation";
import { SignUpMethodSelector } from "./components/SignUpMethodSelector";
import { EmailSignUpForm } from "./components/EmailSignUpForm";
import { TaxProConfirmation } from "./components/TaxProConfirmation";
import { TermsAndConditions } from "./components/TermsAndConditions";

const CreateAccount = ({ redirect = true, onSuccess }: CreateAccountProps) => {
  const location = useLocation();
  const { businessSlug } = useParams();
  const queryParams = new URLSearchParams(location.search);

  const assigneeId = safeGetParam(queryParams, "assigneeId");
  const token = safeGetParam(queryParams, "token");
  const queryEmail = safeGetParam(queryParams, "email")?.replace(" ", "+");
  const fname = safeGetParam(queryParams, "fname");
  const lname = safeGetParam(queryParams, "lname");
  const phone = safeGetParam(queryParams, "phone");
  const taxProFname = safeGetParam(queryParams, "firstName");
  const taxProLname = safeGetParam(queryParams, "lastName");
  const referrerId = safeGetParam(queryParams, "referrerId");

  // Fetch branding based on businessSlug or referrerId
  const { businessInfo: slugBusinessInfo, loading: brandingLoading } =
    useBranding();
  const [referrerBrandingInfo, setReferrerBrandingInfo] = useState<any>(null);
  const [referrerBrandingLoading, setReferrerBrandingLoading] = useState(false);

  // Determine which branding to use
  const businessInfo = referrerId ? referrerBrandingInfo : slugBusinessInfo;
  const isLoadingBranding = brandingLoading || referrerBrandingLoading;

  // Use businessSlug from referrer branding if available, otherwise use URL param
  const effectiveBusinessSlug =
    businessSlug || referrerBrandingInfo?.businessSlug;

  const taxProName =
    taxProFname && taxProLname
      ? `${taxProFname} ${taxProLname}`
      : taxProFname
        ? taxProFname
        : referrerBrandingInfo?.referrer?.firstName &&
            referrerBrandingInfo?.referrer?.lastName
          ? `${referrerBrandingInfo.referrer.firstName} ${referrerBrandingInfo.referrer.lastName}`
          : referrerBrandingInfo?.referrer?.firstName
            ? referrerBrandingInfo.referrer.firstName
            : "Your Tax Pro";

  const [businessContent] = useState<BusinessContent>({
    businessName: businessInfo?.businessName ?? "",
    businessOwner: "",
    businessLogo: businessInfo?.logoUrl ?? "",
    loading: brandingLoading,
  });

  // Use shared account creation store
  const {
    formData,
    error,
    notification,
    loading,
    googleLoading,
    showVerification,
    signUpMethod,
    isTaxProConfirmed,
    setFormData,
    setFormField,
    setSignUpMethod,
    setGoogleLoading,
    setIsTaxProConfirmed,
    setNotification,
  } = useAccountCreationStore();

  // Initialize form data from query params
  useEffect(() => {
    if (fname || lname || queryEmail || phone) {
      setFormData({
        firstName: fname ?? "",
        lastName: lname ?? "",
        email: queryEmail ?? "",
        phoneNumber: phone ?? "",
      });
    }
  }, [fname, lname, queryEmail, phone, setFormData]);

  // Fetch referrer branding when referrerId is present
  useEffect(() => {
    if (referrerId) {
      setReferrerBrandingLoading(true);
      getReferrerBranding(referrerId)
        .then((data) => {
          if (data) {
            setReferrerBrandingInfo(data);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch referrer branding:", error);
        })
        .finally(() => {
          setReferrerBrandingLoading(false);
        });
    }
  }, [referrerId]);

  const userType = determineUserType(businessSlug ?? null, token, roles);

  // Use shared account creation logic
  const { handleEmailChange, validateAndSubmit, allRequirementsMet } =
    useAccountCreation({
      userType,
      token,
      businessSlug,
      assigneeId,
      referrerId,
      redirect,
      onSuccess,
    });

  // Google Sign-Up handler
  const handleGoogleSignUpSuccess = async (response: any) => {
    // Check if this is a new user (from Google account creation)
    const isNewUser = response.content?.isNewUser;

    if (token) {
      // Staff account creation - redirect to home
      window.location.href = `/`;
    } else if (redirect) {
      // Regular account creation - skip verification for Google accounts
      // Google emails are pre-verified
      if (isNewUser) {
        // New account created - skip verification
        window.location.href = AppRoutes.root;
      } else {
        // Existing user logged in
        window.location.href = AppRoutes.root;
      }
    } else {
      // TaxPro flow - call onSuccess to proceed to plan selection, then onboarding
      // Keep loading state true during redirect to purchase subscription page
      onSuccess?.(response.content);
    }
  };

  const handleGoogleSignUpError = (error: any) => {
    setGoogleLoading(false);
    setNotification({
      title: "Google Sign-Up Failed",
      message:
        error.content?.message ||
        error.message ||
        "Failed to sign up with Google",
      color: "red",
      loading: false,
    });
  };

  const { triggerSignIn: triggerGoogleSignUp } = useGoogleSignIn(
    handleGoogleSignUpSuccess,
    handleGoogleSignUpError,
    {
      businessSlug: businessSlug ?? undefined,
      userType,
      businessId: businessSlug ?? undefined,
      assigneeId,
      token,
      firstName: fname,
      lastName: lname,
      phoneNumber: phone,
      referrerId,
    }
  );

  // Wrapper to set loading state when Google sign-up is triggered
  const handleGoogleSignUpClick = () => {
    setGoogleLoading(true);
    triggerGoogleSignUp();
  };

  const isStaff = userType === roles.Staff[0];
  const isClient = userType === roles.Client;
  const infoText = generateWelcomeMessage(userType, {
    businessName: businessContent?.businessName,
    taxProName,
    businessOwner: businessContent?.businessOwner,
  });

  const disableButtonDueToPassword = !allRequirementsMet;
  const disableButton =
    disableButtonDueToPassword ||
    loading ||
    (!effectiveBusinessSlug && !isTaxProConfirmed);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await validateAndSubmit();
  };

  const headerMessage = () => {
    if (isStaff) {
      return businessInfo?.businessName
        ? `Welcome to the ${businessInfo?.businessName} team!`
        : "Welcome to the team!";
    } else if (isClient) {
      return "Sign Up For Your Biggest Refund!";
    } else {
      return "Join Thousands of Tax Pros Using SmartWiz";
    }
  };

  // Ensure we have a valid hex color before calling lightenHex
  const primaryColor =
    businessInfo?.theme?.primaryColor || DEFAULT_PRIMARY_COLOR;
  const validHexColor =
    primaryColor && primaryColor.startsWith("#")
      ? primaryColor
      : DEFAULT_PRIMARY_COLOR;

  const lighterColor = lightenHex(validHexColor, 0.5);

  if (isLoadingBranding) {
    return (
      <Container
        className=""
        size="sm"
        style={{ textAlign: "center", height: "100%" }}
      >
        <Center style={{ height: "100vh" }}>
          <SmartWizLogo /> <Loader size="md" color="blue" ml={10} />
        </Center>
      </Container>
    );
  }

  if (showVerification) {
    return (
      <AccountVerification
        redirectPath={AppRoutes.root}
        email={formData.email}
        businessSlug={effectiveBusinessSlug ?? undefined}
      />
    );
  }

  return (
    <div
      className="login-container"
      style={{ flexDirection: effectiveBusinessSlug ? "row" : "row-reverse" }}
    >
      <HeaderSection
        businessInfo={businessInfo}
        businessSlug={effectiveBusinessSlug}
        lighterColor={lighterColor}
      />
      <div className="auth-form-container">
        <form onSubmit={submit}>
          <div className="mobile-fallback-logo">
            {businessInfo?.logoUrl ? (
              <Image
                src={businessInfo?.logoUrl}
                alt="Business Logo"
                height={72}
                width={72}
                radius="md"
                fallbackSrc={genericLogo}
              />
            ) : effectiveBusinessSlug ? (
              <GenericLogo color={validHexColor} width={72} height={72} />
            ) : (
              ""
            )}
          </div>
          {assigneeId && taxProName && (
            <Text ta="center" size="xl" className="create-account-title">
              <Text span fw={700}>
                {taxProName}
              </Text>{" "}
              has invited you to
            </Text>
          )}
          <h1 className="create-account-title">{headerMessage()}</h1>
          <Stack className="stack" gap="md">
            <Text
              className="info-text"
              c="dimmed"
              style={{ marginTop: "0.5rem" }}
            >
              <span dangerouslySetInnerHTML={{ __html: infoText }} />
            </Text>

            {signUpMethod === null ? (
              // Initial state: Show only two sign-up options
              <SignUpMethodSelector
                onGoogleSignUp={handleGoogleSignUpClick}
                onEmailSignUp={() => setSignUpMethod("email")}
                googleLoading={googleLoading}
                primaryColor={validHexColor}
              />
            ) : (
              // Email sign-up: Show form fields
              <EmailSignUpForm
                formData={formData}
                error={error}
                onFormDataChange={setFormField}
                onEmailBlur={handleEmailChange}
                onBack={() => setSignUpMethod(null)}
                onSubmit={validateAndSubmit}
                queryEmail={queryEmail}
                showUserBusinessName={!effectiveBusinessSlug}
                disableButton={disableButton}
                disableButtonDueToPassword={disableButtonDueToPassword}
                effectiveBusinessSlug={effectiveBusinessSlug}
                isTaxProConfirmed={isTaxProConfirmed}
                primaryColor={validHexColor}
                submitLabel={!redirect ? `Sign Up` : `Create Account`}
              >
                {!effectiveBusinessSlug && (
                  <TaxProConfirmation
                    isTaxProConfirmed={isTaxProConfirmed}
                    onConfirmationChange={setIsTaxProConfirmed}
                  />
                )}
              </EmailSignUpForm>
            )}

            <TermsAndConditions primaryColor={validHexColor} />

            {!loading && !isStaff && (
              <Anchor
                className="link"
                href={
                  effectiveBusinessSlug
                    ? `/${effectiveBusinessSlug}${AppRoutes.login}`
                    : AppRoutes.login
                }
                c={validHexColor}
              >
                I already have an account
              </Anchor>
            )}
            {notification && (
              <Notification
                title={notification.title}
                color={notification.color}
                onClose={() => setNotification(null)}
                withCloseButton
                loading={notification.loading}
              >
                {notification.message}
              </Notification>
            )}
          </Stack>
        </form>
      </div>
    </div>
  );
};

export default CreateAccount;
